import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

function buildCommonQueryParams({ storeId, startDate, endDate } = {}) {
  const qp = new URLSearchParams();
  if (storeId !== undefined && storeId !== null && String(storeId).trim() !== "") {
    qp.set("store_id", String(storeId));
  }
  if (startDate) qp.set("start_date", String(startDate));
  if (endDate) qp.set("end_date", String(endDate));
  return qp.toString();
}

function buildDashboardQueryParams({ storeIds, startDate, endDate } = {}) {
  // Фильтр «Магазины» на странице продаж — мультиселект, поэтому `store_ids`
  // повторяется столько раз, сколько магазинов выбрано. Пустой список = все магазины.
  const qp = new URLSearchParams();
  (storeIds || []).forEach((id) => {
    if (id !== undefined && id !== null && String(id).trim() !== "") {
      qp.append("store_ids", String(id));
    }
  });
  if (startDate) qp.set("start_date", String(startDate));
  if (endDate) qp.set("end_date", String(endDate));
  return qp;
}

export const fetchPnlReport = createAsyncThunk(
  "reports/fetchPnl",
  async ({ storeId, startDate, endDate } = {}) => {
    const qs = buildCommonQueryParams({ storeId, startDate, endDate });
    return apiFetch(`/api/reports/pnl/${qs ? `?${qs}` : ""}`, { method: "GET" });
  }
);

export const fetchWholesaleSalesReport = createAsyncThunk(
  "reports/fetchWholesaleSales",
  async ({ storeId, customerId, startDate, endDate } = {}) => {
    const qp = new URLSearchParams(buildCommonQueryParams({ storeId, startDate, endDate }));
    if (customerId !== undefined && customerId !== null && String(customerId).trim() !== "") {
      qp.set("customer_id", String(customerId));
    }
    const qs = qp.toString();
    return apiFetch(`/api/reports/wholesale-sales/${qs ? `?${qs}` : ""}`, {
      method: "GET",
    });
  }
);

export const fetchOnlineSalesReport = createAsyncThunk(
  "reports/fetchOnlineSales",
  async ({ storeId, storeIds, startDate, endDate } = {}) => {
    const qp = buildDashboardQueryParams({ storeIds, startDate, endDate });
    if (storeId !== undefined && storeId !== null && String(storeId).trim() !== "") {
      qp.set("store_id", String(storeId));
    }
    const qs = qp.toString();
    return apiFetch(`/api/reports/online-sales/${qs ? `?${qs}` : ""}`, {
      method: "GET",
    });
  }
);

export const fetchInventoryRevisionsReport = createAsyncThunk(
  "reports/fetchInventoryRevisions",
  async ({ storeId, startDate, endDate, status, diffReason, dateField } = {}) => {
    const qp = new URLSearchParams(buildCommonQueryParams({ storeId, startDate, endDate }));
    if (status !== undefined && status !== null && String(status).trim() !== "") {
      qp.set("status", String(status).trim());
    }
    if (diffReason !== undefined && diffReason !== null && String(diffReason).trim() !== "") {
      qp.set("diff_reason", String(diffReason).trim());
    }
    if (dateField) qp.set("date_field", String(dateField));
    const qs = qp.toString();
    return apiFetch(`/api/reports/inventory-revisions/${qs ? `?${qs}` : ""}`, {
      method: "GET",
    });
  }
);

export const fetchSalesDashboard = createAsyncThunk(
  "reports/fetchSalesDashboard",
  async ({ storeIds, startDate, endDate, topProducts } = {}) => {
    const qp = buildDashboardQueryParams({ storeIds, startDate, endDate });
    if (topProducts) qp.set("top_products", String(topProducts));
    const qs = qp.toString();
    return apiFetch(`/api/reports/sales-dashboard/${qs ? `?${qs}` : ""}`, { method: "GET" });
  }
);

export const fetchOpenShiftsReport = createAsyncThunk(
  "reports/fetchOpenShifts",
  async ({ storeIds, limit } = {}) => {
    const qp = buildDashboardQueryParams({ storeIds });
    if (limit) qp.set("limit", String(limit));
    const qs = qp.toString();
    return apiFetch(`/api/reports/open-shifts/${qs ? `?${qs}` : ""}`, { method: "GET" });
  }
);

export const fetchCashierPerformanceReport = createAsyncThunk(
  "reports/fetchCashierPerformance",
  async ({ storeIds, startDate, endDate, sortBy, limit } = {}) => {
    const qp = buildDashboardQueryParams({ storeIds, startDate, endDate });
    if (sortBy) qp.set("sort_by", String(sortBy));
    if (limit) qp.set("limit", String(limit));
    const qs = qp.toString();
    return apiFetch(`/api/reports/cashier-performance/${qs ? `?${qs}` : ""}`, { method: "GET" });
  }
);

export const fetchShiftsReport = createAsyncThunk(
  "reports/fetchShifts",
  async ({ storeIds, startDate, endDate, limit } = {}) => {
    const qp = buildDashboardQueryParams({ storeIds, startDate, endDate });
    if (limit) qp.set("limit", String(limit));
    const qs = qp.toString();
    return apiFetch(`/api/reports/shifts/${qs ? `?${qs}` : ""}`, { method: "GET" });
  }
);

export const fetchSalesTrendsReport = createAsyncThunk(
  "reports/fetchSalesTrends",
  async ({ storeIds, startDate, endDate, topN } = {}) => {
    const qp = buildDashboardQueryParams({ storeIds, startDate, endDate });
    if (topN) qp.set("top_n", String(topN));
    const qs = qp.toString();
    return apiFetch(`/api/reports/sales-trends/${qs ? `?${qs}` : ""}`, { method: "GET" });
  }
);

export const fetchWarehouseReport = createAsyncThunk(
  "reports/fetchWarehouse",
  async ({ storeIds, startDate, endDate, topProducts, topDefective } = {}) => {
    const qp = buildDashboardQueryParams({ storeIds, startDate, endDate });
    if (topProducts) qp.set("top_products", String(topProducts));
    if (topDefective) qp.set("top_defective", String(topDefective));
    const qs = qp.toString();
    return apiFetch(`/api/reports/warehouse/${qs ? `?${qs}` : ""}`, { method: "GET" });
  }
);

export const fetchHomeReport = createAsyncThunk(
  "reports/fetchHome",
  async ({ storeIds, day, topProducts } = {}) => {
    const qp = buildDashboardQueryParams({ storeIds });
    if (day) qp.set("day", String(day));
    if (topProducts) qp.set("top_products", String(topProducts));
    const qs = qp.toString();
    return apiFetch(`/api/reports/home/${qs ? `?${qs}` : ""}`, { method: "GET" });
  }
);

export const fetchAccountingReport = createAsyncThunk(
  "reports/fetchAccounting",
  async ({ storeIds, startDate, endDate, weeks } = {}) => {
    const qp = buildDashboardQueryParams({ storeIds, startDate, endDate });
    if (weeks) qp.set("weeks", String(weeks));
    const qs = qp.toString();
    return apiFetch(`/api/reports/accounting/${qs ? `?${qs}` : ""}`, { method: "GET" });
  }
);

const slice = createSlice({
  name: "reports",
  initialState: {
    pnl: { status: "idle", error: null, data: null },
    wholesaleSales: { status: "idle", error: null, data: null },
    onlineSales: { status: "idle", error: null, data: null },
    inventoryRevisions: { status: "idle", error: null, data: null },
    salesDashboard: { status: "idle", error: null, data: null },
    openShifts: { status: "idle", error: null, data: null },
    cashierPerformance: { status: "idle", error: null, data: null },
    shifts: { status: "idle", error: null, data: null },
    salesTrends: { status: "idle", error: null, data: null },
    warehouse: { status: "idle", error: null, data: null },
    home: { status: "idle", error: null, data: null },
    accounting: { status: "idle", error: null, data: null },
  },
  reducers: {
    resetPnlReport(state) {
      state.pnl = { status: "idle", error: null, data: null };
    },
    resetWholesaleSalesReport(state) {
      state.wholesaleSales = { status: "idle", error: null, data: null };
    },
    resetOnlineSalesReport(state) {
      state.onlineSales = { status: "idle", error: null, data: null };
    },
    resetInventoryRevisionsReport(state) {
      state.inventoryRevisions = { status: "idle", error: null, data: null };
    },
    resetSalesDashboardReport(state) {
      state.salesDashboard = { status: "idle", error: null, data: null };
    },
    resetOpenShiftsReport(state) {
      state.openShifts = { status: "idle", error: null, data: null };
    },
    resetCashierPerformanceReport(state) {
      state.cashierPerformance = { status: "idle", error: null, data: null };
    },
    resetShiftsReport(state) {
      state.shifts = { status: "idle", error: null, data: null };
    },
    resetSalesTrendsReport(state) {
      state.salesTrends = { status: "idle", error: null, data: null };
    },
    resetWarehouseReport(state) {
      state.warehouse = { status: "idle", error: null, data: null };
    },
    resetHomeReport(state) {
      state.home = { status: "idle", error: null, data: null };
    },
    resetAccountingReport(state) {
      state.accounting = { status: "idle", error: null, data: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPnlReport.pending, (state) => {
        state.pnl.status = "loading";
        state.pnl.error = null;
      })
      .addCase(fetchPnlReport.fulfilled, (state, action) => {
        state.pnl.status = "succeeded";
        state.pnl.data = action.payload;
      })
      .addCase(fetchPnlReport.rejected, (state, action) => {
        state.pnl.status = "failed";
        state.pnl.error = action.error?.message || "Не удалось загрузить отчёт";
      })
      .addCase(fetchWholesaleSalesReport.pending, (state) => {
        state.wholesaleSales.status = "loading";
        state.wholesaleSales.error = null;
      })
      .addCase(fetchWholesaleSalesReport.fulfilled, (state, action) => {
        state.wholesaleSales.status = "succeeded";
        state.wholesaleSales.data = action.payload;
      })
      .addCase(fetchWholesaleSalesReport.rejected, (state, action) => {
        state.wholesaleSales.status = "failed";
        state.wholesaleSales.error = action.error?.message || "Не удалось загрузить отчёт";
      })
      .addCase(fetchOnlineSalesReport.pending, (state) => {
        state.onlineSales.status = "loading";
        state.onlineSales.error = null;
      })
      .addCase(fetchOnlineSalesReport.fulfilled, (state, action) => {
        state.onlineSales.status = "succeeded";
        state.onlineSales.data = action.payload;
      })
      .addCase(fetchOnlineSalesReport.rejected, (state, action) => {
        state.onlineSales.status = "failed";
        state.onlineSales.error = action.error?.message || "Не удалось загрузить отчёт";
      })
      .addCase(fetchInventoryRevisionsReport.pending, (state) => {
        state.inventoryRevisions.status = "loading";
        state.inventoryRevisions.error = null;
      })
      .addCase(fetchInventoryRevisionsReport.fulfilled, (state, action) => {
        state.inventoryRevisions.status = "succeeded";
        state.inventoryRevisions.data = action.payload;
      })
      .addCase(fetchInventoryRevisionsReport.rejected, (state, action) => {
        state.inventoryRevisions.status = "failed";
        state.inventoryRevisions.error =
          action.error?.message || "Не удалось загрузить отчёт по ревизиям";
      })
      .addCase(fetchSalesDashboard.pending, (state) => {
        state.salesDashboard.status = "loading";
        state.salesDashboard.error = null;
      })
      .addCase(fetchSalesDashboard.fulfilled, (state, action) => {
        state.salesDashboard.status = "succeeded";
        state.salesDashboard.data = action.payload;
      })
      .addCase(fetchSalesDashboard.rejected, (state, action) => {
        state.salesDashboard.status = "failed";
        state.salesDashboard.error = action.error?.message || "Не удалось загрузить отчёт по продажам";
      })
      .addCase(fetchOpenShiftsReport.pending, (state) => {
        state.openShifts.status = "loading";
        state.openShifts.error = null;
      })
      .addCase(fetchOpenShiftsReport.fulfilled, (state, action) => {
        state.openShifts.status = "succeeded";
        state.openShifts.data = action.payload;
      })
      .addCase(fetchOpenShiftsReport.rejected, (state, action) => {
        state.openShifts.status = "failed";
        state.openShifts.error = action.error?.message || "Не удалось загрузить открытые смены";
      })
      .addCase(fetchCashierPerformanceReport.pending, (state) => {
        state.cashierPerformance.status = "loading";
        state.cashierPerformance.error = null;
      })
      .addCase(fetchCashierPerformanceReport.fulfilled, (state, action) => {
        state.cashierPerformance.status = "succeeded";
        state.cashierPerformance.data = action.payload;
      })
      .addCase(fetchCashierPerformanceReport.rejected, (state, action) => {
        state.cashierPerformance.status = "failed";
        state.cashierPerformance.error = action.error?.message || "Не удалось загрузить отчёт по кассирам";
      })
      .addCase(fetchShiftsReport.pending, (state) => {
        state.shifts.status = "loading";
        state.shifts.error = null;
      })
      .addCase(fetchShiftsReport.fulfilled, (state, action) => {
        state.shifts.status = "succeeded";
        state.shifts.data = action.payload;
      })
      .addCase(fetchShiftsReport.rejected, (state, action) => {
        state.shifts.status = "failed";
        state.shifts.error = action.error?.message || "Не удалось загрузить отчёт по сменам";
      })
      .addCase(fetchSalesTrendsReport.pending, (state) => {
        state.salesTrends.status = "loading";
        state.salesTrends.error = null;
      })
      .addCase(fetchSalesTrendsReport.fulfilled, (state, action) => {
        state.salesTrends.status = "succeeded";
        state.salesTrends.data = action.payload;
      })
      .addCase(fetchSalesTrendsReport.rejected, (state, action) => {
        state.salesTrends.status = "failed";
        state.salesTrends.error = action.error?.message || "Не удалось загрузить динамику продаж";
      })
      .addCase(fetchWarehouseReport.pending, (state) => {
        state.warehouse.status = "loading";
        state.warehouse.error = null;
      })
      .addCase(fetchWarehouseReport.fulfilled, (state, action) => {
        state.warehouse.status = "succeeded";
        state.warehouse.data = action.payload;
      })
      .addCase(fetchWarehouseReport.rejected, (state, action) => {
        state.warehouse.status = "failed";
        state.warehouse.error = action.error?.message || "Не удалось загрузить отчёт по складу";
      })
      .addCase(fetchHomeReport.pending, (state) => {
        state.home.status = "loading";
        state.home.error = null;
      })
      .addCase(fetchHomeReport.fulfilled, (state, action) => {
        state.home.status = "succeeded";
        state.home.data = action.payload;
      })
      .addCase(fetchHomeReport.rejected, (state, action) => {
        state.home.status = "failed";
        state.home.error = action.error?.message || "Не удалось загрузить сводку";
      })
      .addCase(fetchAccountingReport.pending, (state) => {
        state.accounting.status = "loading";
        state.accounting.error = null;
      })
      .addCase(fetchAccountingReport.fulfilled, (state, action) => {
        state.accounting.status = "succeeded";
        state.accounting.data = action.payload;
      })
      .addCase(fetchAccountingReport.rejected, (state, action) => {
        state.accounting.status = "failed";
        state.accounting.error = action.error?.message || "Не удалось загрузить обзор бухгалтерии";
      });
  },
});

export const {
  resetPnlReport,
  resetWholesaleSalesReport,
  resetOnlineSalesReport,
  resetInventoryRevisionsReport,
  resetSalesDashboardReport,
  resetOpenShiftsReport,
  resetCashierPerformanceReport,
  resetShiftsReport,
  resetSalesTrendsReport,
  resetWarehouseReport,
  resetHomeReport,
  resetAccountingReport,
} = slice.actions;
export default slice.reducer;

export const selectPnlReport = (state) => state.reports.pnl;
export const selectPnlData = (state) => state.reports.pnl.data;
export const selectPnlStatus = (state) => state.reports.pnl.status;
export const selectPnlError = (state) => state.reports.pnl.error;

export const selectWholesaleSalesReport = (state) => state.reports.wholesaleSales;
export const selectWholesaleSalesData = (state) => state.reports.wholesaleSales.data;
export const selectWholesaleSalesStatus = (state) => state.reports.wholesaleSales.status;
export const selectWholesaleSalesError = (state) => state.reports.wholesaleSales.error;

export const selectOnlineSalesReport = (state) => state.reports.onlineSales;
export const selectOnlineSalesData = (state) => state.reports.onlineSales.data;
export const selectOnlineSalesStatus = (state) => state.reports.onlineSales.status;
export const selectOnlineSalesError = (state) => state.reports.onlineSales.error;

export const selectInventoryRevisionsReport = (state) => state.reports.inventoryRevisions;
export const selectInventoryRevisionsReportData = (state) =>
  state.reports.inventoryRevisions.data;
export const selectInventoryRevisionsReportStatus = (state) =>
  state.reports.inventoryRevisions.status;
export const selectInventoryRevisionsReportError = (state) =>
  state.reports.inventoryRevisions.error;

export const selectSalesDashboardData = (state) => state.reports.salesDashboard.data;
export const selectSalesDashboardStatus = (state) => state.reports.salesDashboard.status;
export const selectSalesDashboardError = (state) => state.reports.salesDashboard.error;

export const selectOpenShiftsData = (state) => state.reports.openShifts.data;
export const selectOpenShiftsStatus = (state) => state.reports.openShifts.status;
export const selectOpenShiftsError = (state) => state.reports.openShifts.error;

export const selectCashierPerformanceData = (state) => state.reports.cashierPerformance.data;
export const selectCashierPerformanceStatus = (state) => state.reports.cashierPerformance.status;
export const selectCashierPerformanceError = (state) => state.reports.cashierPerformance.error;

export const selectShiftsData = (state) => state.reports.shifts.data;
export const selectShiftsStatus = (state) => state.reports.shifts.status;
export const selectShiftsError = (state) => state.reports.shifts.error;

export const selectSalesTrendsData = (state) => state.reports.salesTrends.data;
export const selectSalesTrendsStatus = (state) => state.reports.salesTrends.status;
export const selectSalesTrendsError = (state) => state.reports.salesTrends.error;

export const selectWarehouseData = (state) => state.reports.warehouse.data;
export const selectWarehouseStatus = (state) => state.reports.warehouse.status;
export const selectWarehouseError = (state) => state.reports.warehouse.error;

export const selectHomeData = (state) => state.reports.home.data;
export const selectHomeStatus = (state) => state.reports.home.status;
export const selectHomeError = (state) => state.reports.home.error;

export const selectAccountingData = (state) => state.reports.accounting.data;
export const selectAccountingStatus = (state) => state.reports.accounting.status;
export const selectAccountingError = (state) => state.reports.accounting.error;
